import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFAQs = () => {
  const { getAuthHeaders } = useAdmin();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faqs`, getAuthHeaders());
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
    setLoading(false);
  };

  const handleEdit = (faq) => {
    setEditing(faq.id);
    setFormData(faq);
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.id || dataToSave.id === 'new') {
        dataToSave.id = crypto.randomUUID();
      }
      
      if (editing === 'new') {
        await axios.post(`${API}/admin/faqs`, dataToSave, getAuthHeaders());
      } else {
        await axios.put(`${API}/admin/faqs/${formData.id}`, dataToSave, getAuthHeaders());
      }
      await fetchFAQs();
      setEditing(null);
      setFormData({});
      alert('FAQ saved successfully!');
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Error saving FAQ: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      try {
        await axios.delete(`${API}/admin/faqs/${id}`, getAuthHeaders());
        await fetchFAQs();
        alert('FAQ deleted successfully!');
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        alert('Error deleting FAQ');
      }
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      id: '',
      question: '',
      answer: ''
    });
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="display-medium text-white">FAQs Management</h1>
          <button onClick={handleNew} className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Add New FAQ
          </button>
        </div>

        {editing && (
          <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
            <h2 className="heading-2 text-white mb-4">{editing === 'new' ? 'New FAQ' : 'Edit FAQ'}</h2>
            <div className="space-y-4">
              <div>
                <label className="body-small text-white mb-1 block">Question</label>
                <input
                  type="text"
                  value={formData.question || ''}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="Enter question"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Answer</label>
                <textarea
                  value={formData.answer || ''}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded h-32"
                  placeholder="Enter answer"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                <Save size={20} /> Save
              </button>
              <button onClick={() => { setEditing(null); setFormData({}); }} className="btn-secondary flex items-center gap-2">
                <X size={20} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white/5 border border-white/25 p-6 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="heading-3 text-white mb-2">{faq.question}</h3>
                  <p className="body-small text-white/85">{faq.answer}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(faq)} className="text-[#00FFD1] hover:text-[#6FD2C0]">
                    <Edit size={20} />
                  </button>
                  <button onClick={() => handleDelete(faq.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFAQs;
